import {
  Box,
  Grid,
  Table,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { addDays, format, getDate, getMonth } from "date-fns";
import React, { PureComponent, useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Loader } from "../../components/Loader";
import { Title } from "../../components/Title";
import {
  useSubscriptionsByTierQuery,
  useSubscriptionsReportLazyQuery,
} from "../../graphql";
import { DatesFilter } from "../Call/filter";

const renderActiveShape = (props) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
        {payload.tierName}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill="#333"
      >{`Paketa "${payload.tierName}" - ${value} abonime`}</text>
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={18}
        textAnchor={textAnchor}
        fill="#999"
      >
        {`(Përqindja ${(percent * 100).toFixed(2)}%)`}
      </text>
    </g>
  );
};

const SubscriptionsByTier = ({ data, loading }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  if (loading) {
    return <Loader />;
  }

  return (
    <PieChart width={800} height={400}>
      <Pie
        activeIndex={activeIndex}
        activeShape={renderActiveShape}
        data={data.subscriptionsByTier}
        cx="50%"
        cy="50%"
        innerRadius={60}
        outerRadius={80}
        fill="#205aa7"
        dataKey="subscriptions"
        onMouseEnter={(_, index) => {
          setActiveIndex(index);
        }}
      >
        {data?.subscriptionsByTier.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
        ))}
      </Pie>
    </PieChart>
  );
};

const SubscriptionsReport: React.FC = () => {
  const [startDate, setStartDate] = useState(addDays(new Date(), -30));
  const [endDate, setEndDate] = useState(addDays(new Date(), 0));

  const [subscriptionsReport, { data, loading }] =
    useSubscriptionsReportLazyQuery({});
  const { data: subscriptionsByTierData, loading: loadingSubscriptionsByData } =
    useSubscriptionsByTierQuery();

  useEffect(() => {
    subscriptionsReport({
      variables: {
        range: {
          from: format(addDays(startDate, -1), "yyyy-MM-dd"),
          to: format(addDays(endDate, 1), "yyyy-MM-dd"),
        },
      },
    });
    // eslint-disable-next-line
  }, []);

  const chartData = data?.subscriptionsReport?.map((i) => {
    const date = new Date(i.date);
    const month = getMonth(date);
    const day = getDate(date);

    return {
      name: `${day}/${month + 1}`,
      abonime: i.value,
    };
  });

  return (
    <>
      <Box borderWidth="1px" borderRadius="lg" background="#FFF" p="10px">
        <Title title="Abonime sipas datave">
          <Box marginRight="150px">
            <DatesFilter
              onChange={(i) => {
                subscriptionsReport({
                  variables: {
                    range: {
                      from: format(addDays(i.startDate, -1), "yyyy-MM-dd"),
                      to: format(addDays(i.endDate, 1), "yyyy-MM-dd"),
                    },
                  },
                });
              }}
            ></DatesFilter>
          </Box>
        </Title>

        {loading ? (
          <Loader />
        ) : (
          <ResponsiveContainer width="100%" height={500}>
            <BarChart
              width={700}
              height={500}
              data={chartData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              {/* <Legend /> */}
              <Bar dataKey="abonime" fill="#205aa7" />
            </BarChart>
          </ResponsiveContainer>
        )}

        <Title title="Abonime aktive sipas paketave"></Title>

        <Grid templateColumns="repeat(2, 1fr)" gap={6}>
          <Box w="100%">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Paketa</Th>
                  <Th isNumeric>Abonime</Th>
                </Tr>
              </Thead>
              <Tbody>
                {subscriptionsByTierData?.subscriptionsByTier.map((item) => (
                  <Tr>
                    <Td>{item.tierName}</Td>
                    <Td isNumeric>{item.subscriptions}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <Box w="100%">
            <SubscriptionsByTier
              data={subscriptionsByTierData}
              loading={loadingSubscriptionsByData}
            />
          </Box>
        </Grid>
      </Box>
    </>
  );
};

export default SubscriptionsReport;
